import React, { useState } from "react";
import { Container, Paper, Typography, Box, TextField, Button, Alert, AppBar, Toolbar, Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { runAuthDiagnostic } from "../utils/authDiagnostic";

const Login = () => {
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors } } = useForm();
    const { login, loginWithGoogle, loginWithMicrosoft } = useAuth();
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const onSubmit = async (data) => {
        try {
            setError("");
            setLoading(true);
            await login(data.email, data.password);
            navigate("/dashboard");
        } catch (err) {
            setError(t("loginError"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => navigate("/")}> 
                        <img src="/logo.svg" alt="CAPIVIO Logo" style={{ height: "35px", width: "35px", marginRight: "10px" }} />
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                fontWeight: 900, // Only one fontWeight key
                                color: "#DBC078",
                                background: "linear-gradient(90deg, #DBC078 0%, #d0ad50 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                flexGrow: 1
                            }}
                        >
              CAPIVIO
                        </Typography>
                    </Box>
                    <LanguageSwitcher />
                </Toolbar>
            </AppBar>
            <Box
                sx={{
                    minHeight: "100vh",
                    bgcolor: "#f7f7f7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2
                }}
            >
                <Container maxWidth="md" disableGutters>
                    <Grid container spacing={3} alignItems="center" justifyContent="center">
                        {/* Login Card */}
                        <Grid item xs={12} md={7} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: { xs: 3, sm: 4 },
                                    width: "100%",
                                    maxWidth: 400,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                    borderRadius: 3,
                                    boxSizing: "border-box"
                                }}
                            >
                                <Typography variant="h4" component="h1" align="center" gutterBottom>
                                    {t("login")}
                                </Typography>
                                {error && <Alert severity="error">{error}</Alert>}
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            label={t("email")}
                                            type="email"
                                            autoComplete="email"
                                            {...register("email", { required: t("emailRequired") })}
                                            error={!!errors.email}
                                            helperText={errors.email?.message}
                                        />
                                        <TextField
                                            fullWidth
                                            label={t("password")}
                                            type="password"
                                            autoComplete="current-password"
                                            {...register("password", { required: t("passwordRequired") })}
                                            error={!!errors.password}
                                            helperText={errors.password?.message}
                                        />
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            disabled={loading}
                                        >
                                            {loading ? t("signingIn") : t("login")}
                                        </Button>
                                    </Box>
                                </form>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        fullWidth
                                        onClick={async () => {
                                            setError("");
                                            setLoading(true);
                                            try {
                                                await loginWithGoogle();
                                                navigate("/dashboard");
                                            } catch (err) {
                                                setError(err.message || t("googleSignInError"));
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    >
                                        {t("signInWithGoogle")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        fullWidth
                                        onClick={async () => {
                                            setError("");
                                            setLoading(true);
                                            try {
                                                await loginWithMicrosoft();
                                                navigate("/dashboard");
                                            } catch (err) {
                                                setError(err.message || t("microsoftSignInError"));
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    >
                                        {t("signInWithMicrosoft")}
                                    </Button>
                                    
                                    {/* Botón de diagnóstico temporal */}
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        fullWidth
                                        onClick={async () => {
                                            await runAuthDiagnostic();
                                        }}
                                        sx={{ mt: 2 }}
                                    >
                                        🔍 Diagnóstico de Auth
                                    </Button>
                                </Box>
                                <Button
                                    variant="text"
                                    color="inherit"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    onClick={() => navigate("/")}
                                >
                                    {t("backToHome")}
                                </Button>
                            </Paper>
                        </Grid>
                        {/* Sidebar/Info Card */}
                        <Grid item xs={12} md={5} sx={{ mt: { xs: 4, md: 0 } }}>
                            <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, mb: 3, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: "#111", mb: 2 }}>
                  Welcome Back!
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                  Access your CAPIVIO account, manage your investments, and explore new opportunities.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
};

export default Login;